/**
 * Supplier Return Drawer Component
 *
 * Drawer for displaying supplier return details.
 * @see US-40.3 - Frontend retours fournisseur
 */

import { Check, X, Package, Building2, FileText } from 'lucide-react';
import { Drawer, Button, Badge, Skeleton } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useSupplierReturn, useValidateSupplierReturn, useCancelSupplierReturn, useDeleteSupplierReturn } from '../hooks/useSupplierReturns';
import { DocumentSection } from '@/features/documents';
import {
  SupplierReturnStatus,
  SUPPLIER_RETURN_STATUS_LABELS,
  SUPPLIER_RETURN_STATUS_COLORS,
  SUPPLIER_RETURN_ITEM_REASON_LABELS,
  SupplierReturnItemReason,
} from '@bstock/shared';
import type { SupplierReturnItem } from '@bstock/shared';

interface SupplierReturnDrawerProps {
  returnId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showFinancials?: boolean;
  canValidate?: boolean;
  canDelete?: boolean;
  onEdit?: (id: string) => void;
}

/**
 * Map status to badge variant
 */
function getStatusVariant(status: SupplierReturnStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  const colorMap: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    green: 'success',
    gray: 'neutral',
    red: 'danger',
  };
  return colorMap[SUPPLIER_RETURN_STATUS_COLORS[status]] ?? 'neutral';
}

export function SupplierReturnDrawer({
  returnId,
  open,
  onOpenChange,
  showFinancials = true,
  canValidate = false,
  canDelete = false,
  onEdit,
}: SupplierReturnDrawerProps) {
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const { data: ret, isLoading } = useSupplierReturn(returnId);
  const validateMutation = useValidateSupplierReturn();
  const cancelMutation = useCancelSupplierReturn();
  const deleteMutation = useDeleteSupplierReturn();

  const handleValidate = async () => {
    if (ret && window.confirm(`Valider le retour ${ret.returnNumber} ? Les mouvements de stock seront crees.`)) {
      await validateMutation.mutateAsync(ret.id);
    }
  };

  const handleCancel = async () => {
    if (ret && window.confirm(`Annuler le retour ${ret.returnNumber} ? Des contre-mouvements seront crees.`)) {
      await cancelMutation.mutateAsync({ id: ret.id });
    }
  };

  const handleDelete = async () => {
    if (ret && window.confirm(`Supprimer le retour brouillon ${ret.returnNumber} ?`)) {
      await deleteMutation.mutateAsync(ret.id);
      onOpenChange(false);
    }
  };

  const footer = ret ? (
    <div className="flex gap-3">
      {ret.status === SupplierReturnStatus.DRAFT && onEdit && (
        <Button
          variant="secondary"
          onClick={() => onEdit(ret.id)}
        >
          Modifier
        </Button>
      )}
      {canValidate && ret.status === SupplierReturnStatus.DRAFT && (
        <Button
          variant="primary"
          onClick={handleValidate}
          disabled={validateMutation.isPending}
        >
          <Check className="w-4 h-4" />
          Valider
        </Button>
      )}
      {canDelete && ret.status === SupplierReturnStatus.DRAFT && (
        <Button
          variant="danger"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          Supprimer
        </Button>
      )}
      {canValidate && ret.status === SupplierReturnStatus.VALIDATED && (
        <Button
          variant="ghost"
          onClick={handleCancel}
          disabled={cancelMutation.isPending}
        >
          <X className="w-4 h-4" />
          Annuler
        </Button>
      )}
    </div>
  ) : null;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={ret ? ret.returnNumber : 'Retour fournisseur'}
      description={ret ? formatDateShort(ret.returnDate) : undefined}
      size="wide"
      footer={footer}
    >
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-48" />
          <Skeleton className="h-32" />
        </div>
      ) : ret ? (
        <div className="space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <Badge variant={getStatusVariant(ret.status)}>
              {SUPPLIER_RETURN_STATUS_LABELS[ret.status]}
            </Badge>
            {/* US-SR-CENTRAL.1 : badge "Vers centrale" pour les retours internes. */}
            {ret.isInternalReturn && (
              <Badge variant="info">Vers centrale</Badge>
            )}
          </div>

          {/* Info Section */}
          <div className="bg-neutral-50 rounded-lg p-4">
            <h3 className="font-medium text-dark-500 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Informations
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between text-sm">
                <dt className="text-neutral-500">Date du retour</dt>
                <dd className="text-dark-500">{formatDateShort(ret.returnDate)}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-neutral-500">Fournisseur</dt>
                <dd className="text-dark-500 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-neutral-400" />
                  {ret.supplierName}
                </dd>
              </div>
              {ret.goodsReceiptNumber && (
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">Reception liee</dt>
                  <dd className="text-dark-500">{ret.goodsReceiptNumber}</dd>
                </div>
              )}
              {ret.reason && (
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">Motif</dt>
                  <dd className="text-dark-500">{ret.reason}</dd>
                </div>
              )}
              {showFinancials && (
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">Total HT</dt>
                  <dd className="font-semibold text-primary-600">
                    {formatCentimes(ret.totalExclTax, currency)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Items Section */}
          <div>
            <h3 className="font-medium text-dark-500 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Lignes ({ret.items.length})
            </h3>
            <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-200">
              {ret.items.map((item) => (
                <ReturnItemRow key={item.id} item={item} currency={currency} showFinancials={showFinancials} />
              ))}
            </div>
          </div>

          {/* Notes */}
          {ret.notes && (
            <div>
              <h3 className="font-medium text-dark-500 mb-2">Notes</h3>
              <p className="text-sm text-neutral-600 bg-neutral-50 rounded-lg p-3">
                {ret.notes}
              </p>
            </div>
          )}

          {/* Documents */}
          <DocumentSection
            entityRoute="supplier-returns"
            entityId={ret.id}
          />

          {/* Metadata */}
          <div className="text-xs text-neutral-400 space-y-1 border-t border-neutral-200 pt-4">
            <p>Cree le {formatDateShort(ret.createdAt)}</p>
            {ret.validatedAt && (
              <p>Valide le {formatDateShort(ret.validatedAt)}</p>
            )}
            {ret.status === SupplierReturnStatus.CANCELLED && (
              <p>Annule</p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-neutral-500">Retour fournisseur non trouve</p>
      )}
    </Drawer>
  );
}

/**
 * Return Item Row
 */
function ReturnItemRow({
  item,
  currency,
  showFinancials,
}: {
  item: SupplierReturnItem;
  currency: string;
  showFinancials: boolean;
}) {
  return (
    <div className="p-4 hover:bg-neutral-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-dark-500">{item.rawProductName}</span>
            {item.rawProductSku && (
              <span className="text-xs text-neutral-400">{item.rawProductSku}</span>
            )}
          </div>
          <div className="text-sm text-neutral-500 mt-1">
            {item.packagingName}{showFinancials ? ` - ${formatCentimes(item.unitPriceExclTax, currency)}/u` : ''}
          </div>
        </div>

        <div className="text-right">
          <div className="font-medium text-dark-500">
            {item.quantity}{showFinancials ? ` x ${formatCentimes(item.unitPriceExclTax, currency)}` : ` unite(s)`}
          </div>
          {showFinancials && (
            <div className="text-sm font-semibold text-primary-600">
              {formatCentimes(item.totalPriceExclTax, currency)}
            </div>
          )}
        </div>
      </div>

      {/* Return reason badge */}
      <div className="mt-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
          {SUPPLIER_RETURN_ITEM_REASON_LABELS[item.returnReason as SupplierReturnItemReason] ?? item.returnReason}
        </span>
      </div>
    </div>
  );
}
